/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  message,
  Tabs,
  DatePicker,
  InputNumber,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getNivelesDeEstudios,
  postNivelDeEstudio,
  deleteNivelDeEstudio,
  patchNivelDeEstudio,
  getNivelDeEstudio,
  autorizarNivelDeEstudio,
} from 'api/catalogos/nivelesDeEstudios';
import { checkToken } from 'api/utils';
import { NOMBRES_PATTERN } from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import {
  autorizarContratacion,
  getContratacion, getContratacionesVistaoptimizado, patchContratacion, postContratacion,
} from 'api/Contrataciones';
import { putAutorizarContratacion } from 'api/catalogos/Candidatos';
import moment from 'moment/moment';
import { getEmpleados } from 'api/estructuraOrganizacional/empleados';
import { getAdministracionDePlazas } from 'api/catalogos/admnistracionDePlazas';
import { getPlaza } from 'api/catalogos/plazas';
import { getDatoOperacionalesDeEmpleados } from 'api/estructuraOrganizacional/datosOperacionalesDeEmpleados';

export const niveldeestudioPermission = {
  permissionModel: 'niveldeestudio',
  catalogsModels: [],
  tabsModels: [],
};
const { TabPane } = Tabs;

const Contrataciones = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [esEmpleadoMunicipio, setEsEmpleadoMunicipio] = useState(false);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getContratacionesVistaoptimizado();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getContratacionesVistaoptimizado(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _contratacion = await getContratacion(match.id);
    setTimeout(() => {
      if (_contratacion) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_contratacion));
          setDisableFields(_contratacion?.estados_globales !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });

    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      //  Normalizar fechas: convertir moment a string YYYY-MM-DD
      if (values.fecha_seleccion) {
        values.fecha_seleccion = values.fecha_seleccion.format('YYYY-MM-DD');
      }
      if (values.fecha_contratacion) {
        values.fecha_contratacion = values.fecha_contratacion.format('YYYY-MM-DD');
      }

      //  Normalizar strings vacíos a null (opcional)
      Object.keys(values).forEach((key) => {
        if (values[key] === '') {
          values[key] = null;
        }
      });

      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchContratacion(key, values)
        : postContratacion(values));

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteNivelDeEstudio(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const handleKeyPress = (e) => {
    if (/\d/.test(e.key)) {
      e.preventDefault();
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    if (key) {
      setVisibleAlert(true);
    } else {
      setVisibleAlert(false);
      message.warn('Este campo no se puede borrar');
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se ha seleccionado ninguna contratación.');
        return;
      }
      const saved = await autorizarContratacion(key);
      if (saved) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onOpenConfirmDirecto = () => {
    if (!selectedRowKeys.length) {
      message.warning('Selecciona un candidato.');
      return;
    }
    setVisibleAutorizar(true);
  };

  const onFinishDirecto = async () => {
    try {
      setLoading(true);

      const idDelCandidato = selectedData.candidato_id;

      if (!idDelCandidato) {
        message.error('No se pudo obtener el ID del candidato para esta contratación.');
        setLoading(false);
        return;
      }

      await putAutorizarContratacion(idDelCandidato);

      setVisibleAutorizar(false);
      setSelectedRowKeys([]);
      setSelectedData({});
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al autorizar contratación';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      titleText: 'Candidato',
      children: [
        {
          title: 'Número y Nombre',
          dataIndex: 'candidato_numero_y_nombre_completo',
          key: 'candidato_numero_y_nombre_completo',
          width: 300,
        },
        {
          title: 'Estado',
          dataIndex: 'candidato_estado_del_candidato_descripcion',
          key: 'candidato_estado_del_candidato_descripcion',
          width: 200,
        },
        {
          title: 'Fuente de Reclutamiento',
          dataIndex: 'candidato_fuente_de_reclutamiento_descripcion',
          key: 'candidato_fuente_de_reclutamiento_descripcion',
          width: 200,
        },
      ],
    },
    {
      titleText: 'Cita',
      children: [
        {
          title: 'Fecha y Hora',
          dataIndex: 'cita_fecha_y_hora',
          key: 'cita_fecha_y_hora',
          width: 200,
        },
        {
          title: 'Estado de la Cita',
          dataIndex: 'cita_estado_de_cita_descripcion',
          key: 'cita_estado_de_cita_descripcion',
          width: 200,
        },
      ],
    },
    {
      titleText: 'Resultado Psicométrico',
      children: [
        {
          title: 'Resultado General',
          dataIndex: 'informe_psicometrico_resultado_general_descripcion',
          key: 'informe_psicometrico_resultado_general_descripcion',
          width: 200,
        },
      ],
    },
    {
      titleText: 'Selección y Contratación',
      children: [
        {
          title: 'Seleccionado',
          dataIndex: 'contratacion_seleccionado',
          key: 'contratacion_seleccionado',
          width: 150,
          render: (val) => (val ? 'Sí' : 'No'),
        },
        {
          title: 'Fecha Selección',
          dataIndex: 'contratacion_fecha_seleccion',
          key: 'contratacion_fecha_seleccion',
          width: 200,
        },
        {
          title: 'Contratado',
          dataIndex: 'contratacion_contratado',
          key: 'contratacion_contratado',
          width: 150,
          render: (val) => (val ? 'Sí' : 'No'),
        },
        {
          title: 'Fecha Contratación',
          dataIndex: 'contratacion_fecha_contratacion',
          key: 'contratacion_fecha_contratacion',
          width: 200,
        },
      ],
    },
    {
      titleText: 'Vacante Solicitada',
      children: [
        {
          title: 'Folio',
          dataIndex: 'vacante__folio',
          key: 'vacante__folio',
          width: 100,
        },
        {
          title: 'No. de Oficio',
          dataIndex: 'vacante_no_de_oficio',
          key: 'vacante_no_de_oficio',
          width: 150,
        },
        {
          title: 'Fecha Solicitud',
          dataIndex: 'vacante_fecha_de_solicitud',
          key: 'vacante_fecha_de_solicitud',
          width: 200,
        },
        {
          title: 'Plaza',
          dataIndex: 'vacante_plaza_clave_y_descripcion',
          key: 'vacante_plaza_clave_y_descripcion',
          width: 250,
        },
        {
          title: 'Puesto',
          dataIndex: 'vacante_puesto_clave_y_descripcion',
          key: 'vacante_puesto_clave_y_descripcion',
          width: 250,
        },
        {
          title: 'Área',
          dataIndex: 'vacante_directorio_de_area_clave_y_nombre',
          key: 'vacante_directorio_de_area_clave_y_nombre',
          width: 300,
        },
        {
          title: 'Motivo',
          dataIndex: 'vacante_motivo_de_reclutamiento_nombre',
          key: 'vacante_motivo_de_reclutamiento_nombre',
          width: 300,
        },
        {
          title: 'Estado',
          dataIndex: 'vacante_estado_global_descripcion',
          key: 'vacante_estado_global_descripcion',
          width: 200,
        },
      ],
    },
    {
      titleText: 'Solicitante',
      children: [
        {
          title: 'Solicitante',
          dataIndex: 'solicitante',
          key: 'solicitante',
          width: 300,
        },
        {
          title: 'Plaza',
          dataIndex: 'solicitante_plaza_clave_y_descripcion',
          key: 'solicitante_plaza_clave_y_descripcion',
          width: 250,
        },
        {
          title: 'Puesto',
          dataIndex: 'solicitante_puesto_clave_y_descripcion',
          key: 'solicitante_puesto_clave_y_descripcion',
          width: 250,
        },
        {
          title: 'Área',
          dataIndex: 'solicitante_directorio_de_area_clave_y_nombre',
          key: 'solicitante_directorio_de_area_clave_y_nombre',
          width: 300,
        },
      ],
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    nombre: [
      requiredRule,
      {
        pattern: NOMBRES_PATTERN,
        message: 'Ingrese un nombre válido',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            // onClickAdd,
            onClickEdit: () => onClickEdit(),
            // onClickDelete,
          }}
          customActions={[
            {
              onClick: () => onOpenConfirmDirecto(),
              permission: { model: 'contratacion', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              // disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          width="60%"
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Contratación`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
  )}
          onCancel={onCancel}
          forceRender
        >
          <Tabs defaultActiveKey="1">
            {/* TAB SELECCIÓN (editable con boolean y fecha) */}
            <TabPane tab="Selección" key="1">
              <Form
                layout="vertical"
                form={form}
                onFinish={onFinish}
                scrollToFirstError
                name="seleccion"
                initialValues={{
                  seleccionado: selectedData?.seleccionado ?? false,
                  fecha_seleccion: selectedData?.fecha_seleccion
                    ? moment(selectedData.fecha_seleccion)
                    : null,
                }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Nombre">
                      <Input value={selectedData?.candidato_numero_y_nombre_completo} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Fecha de Nacimiento">
                      <Input value={selectedData?.candidato_fecha_nacimiento} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Edad">
                      <Input value={selectedData?.candidato_edad} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    <Form.Item label="Nivel de Estudios">
                      <Input value={selectedData?.candidato_nivel_estudios_descripcion} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Plaza Solicitada">
                      <Input value={selectedData?.vacante_plaza_clave_y_descripcion} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Puesto Solicitado">
                      <Input value={selectedData?.vacante_puesto_clave_y_descripcion} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Estado Civil">
                      <Input value={selectedData?.candidato_estado_civil_descripcion} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Resultado Psicométrico">
                      <Input
                        value={selectedData?.informe_psicometrico_resultado_general_descripcion}
                        disabled
                      />
                    </Form.Item>
                  </Col>

                  {/* CAMPOS EDITABLES */}
                  <Col span={12}>
                    <Form.Item
                      name="seleccionado"
                      label="Seleccionado"
                      rules={[{ required: true, message: 'Selecciona una opción' }]}
                      hasFeedback
                    >
                      <Select trueFalse disabled={disableFields} />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      shouldUpdate={(prev, curr) => prev.seleccionado !== curr.seleccionado}
                      noStyle
                    >
                      {({ getFieldValue }) => {
                        const seleccionado = getFieldValue('seleccionado');
                        return (
                          <Form.Item
                            name="fecha_seleccion"
                            label="Fecha de Selección"
                            rules={
            seleccionado
              ? [{ required: true, message: 'Selecciona la fecha' }]
              : []
          }
                          >
                            <DatePicker
                              format="DD/MM/YYYY"
                              style={{ width: '100%' }}
                              placeholder={null}
                              disabled={disableFields || !seleccionado}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Col>

                </Row>

                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Form>
            </TabPane>
            {/* TAB CONTRATACIÓN */}
            <TabPane tab="Contratación" key="2">
              <Form
                layout="vertical"
                form={form}
                onFinish={onFinish}
                scrollToFirstError
                name="contratacion"
                initialValues={{
                  estados_globales: ESTATUS_ACTIVO,
                  es_empleado_del_municipio: false,
                }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="contratado"
                      label="Contratado"
                      rules={[{ required: true, message: 'Selecciona una opción' }]}
                      hasFeedback
                    >
                      <Select trueFalse disabled={disableFields} />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      shouldUpdate={(prev, curr) => prev.contratado !== curr.contratado}
                      noStyle
                    >
                      {({ getFieldValue }) => {
                        const contratado = getFieldValue('contratado');
                        return (
                          <Form.Item
                            name="fecha_contratacion"
                            label="Fecha de Contratación"
                            rules={
            contratado
              ? [{ required: true, message: 'Selecciona la fecha' }]
              : []
          }
                          >
                            <DatePicker
                              format="DD/MM/YYYY"
                              style={{ width: '100%' }}
                              placeholder={null}
                              disabled={disableFields || !contratado}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Col>
                  {/* Estatus */}
                  <Col xs={24} sm={24} md={24}>
                    <Form.Item
                      name="estados_globales"
                      label="Estatus"
                      rules={rules.required}
                      hasFeedback
                    >
                      <Select
                        labelProp="descripcion"
                        keyProp="id"
                        dataSource={estados}
                        activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                        disabled={selectedRowKeys.length <= 0 || disableFields}
                        onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                      />
                    </Form.Item>
                  </Col>

                  {/* Es empleado del municipio */}
                  {/* <Col xs={24} sm={24} md={24}>
                    <Form.Item
                      name="es_empleado_del_municipio"
                      label="¿Es empleado actual del Municipio de Tlajomulco de Zúñiga, Jalisco?"
                      rules={[{ required: true, message: 'Selecciona una opción' }]}
                      hasFeedback
                    >
                      <Select
                        trueFalse
                        disabled={disableFields}
                        onChange={(value) => {
                          setEsEmpleadoMunicipio(value);
                          if (!value) {
                            form.setFieldsValue({
                              numero_empleado_actual: null,
                              area: null,
                              plaza: null,
                              puesto: null,
                              sueldo_mensual: null,
                            });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col> */}

                  {/* Número de empleado actual */}
                  {/* <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="numero_empleado_actual"
                      label="Número de Empleado Actual"
                      rules={
      esEmpleadoMunicipio
        ? [{ required: true, message: 'Captura el número de empleado' }]
        : []
    }
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        disabled={!esEmpleadoMunicipio || disableFields}
                        onChange={async (numero) => {
                          if (!numero) {
                            form.setFieldsValue({
                              nombre_empleado: null,
                              area: null,
                              plaza: null,
                              puesto: null,
                              sueldo_mensual: null,
                            });
                            return;
                          }

                          try {
                          //  Buscar empleado por número
                            const empleados = await getEmpleados({ numero_de_empleado: numero });
                            const empleado = empleados?.results?.[0];

                            if (!empleado) {
                              message.warning('No se encontró un empleado con ese número');
                              form.setFieldsValue({
                                nombre_empleado: null,
                                area: null,
                                plaza: null,
                                puesto: null,
                                sueldo_mensual: null,
                              });
                              return;
                            }

                            //  Obtener datos operacionales
                            // eslint-disable-next-line max-len
                            // eslint-disable-next-line max-len
    const [datoOperacional] = await getDatoOperacionalesDeEmpleados({ empleado: empleado.id });

                            let plazaDetalle = null;
                            if (datoOperacional?.plaza) {
                              plazaDetalle = await getPlaza(datoOperacional.plaza);
                            }

                            //  Llenar el formulario
                            form.setFieldsValue({
                              nombre_empleado: empleado.numero_y_nombre_completo || 'N/A',
                              area: plazaDetalle?.directorio_de_area_nombre || 'N/A',
                              plaza: plazaDetalle?.plaza_clave_y_descripcion || 'N/A',
                              puesto: plazaDetalle?.puesto_descripcion || 'N/A',
                              // eslint-disable-next-line max-len
          sueldo_mensual: plazaDetalle?.sueldo_mensual_nominal_asignado || 'N/A',
                            });
                          } catch (err) {
                            message.error('Error al buscar empleado');
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  {esEmpleadoMunicipio && (
                  <>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item name="nombre_empleado" label="Nombre Completo">
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="area" label="Área">
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="plaza" label="Plaza">
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="puesto" label="Puesto">
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="sueldo_mensual" label="Sueldo Mensual">
                          <Input disabled />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          name="motivo_por_el_que_el_empleado_participo"
                          // eslint-disable-next-line max-len
label="Motivo por el que el empleado actual participó en el proceso de Reclutamiento y Selección"
                        >
                          <Input.TextArea rows={3} disabled={disableFields} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                  )}
                  {!esEmpleadoMunicipio && (
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="numero_empleado"
                      label="Número de Empleado"
                      // rules={[
                      //   { required: true, message: 'Captura el número de empleado' },
                      // ]}

                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  )} */}

                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Row>
              </Form>
            </TabPane>

          </Tabs>
        </Modal>

        {/* <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Nivel de Estudio ${form.getFieldValue('nombre')}`}
          loading={loading}
        /> */}
        <ModalConfirmation
          onFinish={onFinishDirecto}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar la contratación de ${selectedData?.candidato_numero_y_nombre_completo || ''}`}
          open={visibleAutorizar}
          loading={loading}
        />
        {/* <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar el Nivel de Estudios ${selectedData?.nombre}`}
          loading={loading}
        /> */}
      </Spin>
    </Row>
  );
};

export default Contrataciones;
